import {
  nonIndividualMdmsQuestionsFatca,
  OnBoardingCompletedReportDetailsType,
  OnBoardingReportDetailsType,
  uboTypes,
} from '../../redux-store/types/api-types';
import { CVL_KRA_REPORT_TYPE, KYC_REPORT_TYPE } from '../../redux-store/types/nct';
import { APPLICATION_STATUS, POA_APPLICATION_STATUS } from '../../utils/constant';
import { formatShortDate } from '../../utils/date';
import { getFieldLabelByKey, getUboDeclarationValues } from '../../utils/utilityFunctions';
import { ColumnType, renderCellValue } from '../DataTable';
import FileSaver from 'file-saver';

export const applicationTypes = [
  { key: 'individual', value: 'Individual (Resident)' },
  { key: 'nonIndividual', value: 'Non-Individual' },
  { key: 'poa', value: 'Individual (POA)' },
  { key: 'nri', value: 'Individual (NRI)' },
  { key: 'nri_poa', value: 'Individual (NRI-POA)' },
];
export const filterApplicationTypes = [
  { key: 'all', value: 'All' },
  { key: 'individual', value: 'Individual (Resident)' },
  { key: 'non_individual', value: 'Non-Individual' },
  { key: 'poa', value: 'Individual (POA)' },
  { key: 'nri', value: 'Individual (NRI)' },
  { key: 'nri_poa', value: 'Individual (NRI-POA)' },
];
const commonHeadersForSummaryReports = [
  {
    header: 'Investor Type',
    label: 'investorType',
  },
  {
    header: 'Total Onboardings',
    label: 'total_onboarded',
  },
  {
    header: 'In Progress',
    label: 'in_progress',
  },
  {
    header: 'Draft',
    label: 'draft',
  },
  {
    header: 'Pending with Investor',
    label: 'pending_with_investor',
  },
  {
    header: 'Pending with IM',
    label: 'pending_with_im',
  },
  {
    header: 'Pending with trustee',
    label: 'pending_with_trustee',
  },
  {
    header: 'Expired',
    label: 'invitationexpired',
  },
  {
    header: 'Rejected',
    label: 'rejected',
  },
  {
    header: 'Completed',
    label: 'completed',
  },
];

const headersForAmendmentReports = [
  { header: 'AMENDMENT NUMBER', label: 'amendmentId' },
  { header: 'INVESTOR NAME', label: 'firstHolderName' },

  { header: 'APPLICATION NUMBER', label: 'applicationNumber' },
  { header: 'DISTRIBUTOR NAME', label: 'distributor' },

  { header: 'STATUS', label: 'status' },
];

const headersForOnboardingCompletedKyc = (
  fatca?: nonIndividualMdmsQuestionsFatca,
  uboTypesMdms?: uboTypes
): ColumnType[] => [
  {
    header: 'S.No.',
    label: 'sno',
    valueGetter: (_: KYC_REPORT_TYPE, index: number): number => Number(index) + 1,
  },
  { header: 'Application Number', label: 'applicationNumber' },
  { header: 'Investor Name', label: 'applicantName' },
  { header: 'Initiated By', label: 'initiatedBy' },
  {
    header: 'Initiation Date',
    label: 'initiationDate',
    valueGetter: ({ initiationDate = '' }: KYC_REPORT_TYPE): string =>
      formatShortDate(initiationDate),
  },
  {
    header: 'Approved By',
    label: 'approvedBy',
  },
  {
    header: 'Approved Date',
    label: 'approvedDate',
    valueGetter: ({ approvedDate = '' }: KYC_REPORT_TYPE): string => formatShortDate(approvedDate),
  },
  {
    header: 'Change Type',
    label: 'fieldName',
    valueGetter: ({ fieldName = '' }: KYC_REPORT_TYPE): string =>
      fatca ? getFieldLabelByKey(fieldName, fatca) : fieldName,
  },
  {
    header: 'Initial Value',
    label: 'oldValue',
    valueGetter: (row: Partial<KYC_REPORT_TYPE>): string =>
      uboTypesMdms ? getUboDeclarationValues(uboTypesMdms, row).oldValue : row.oldValue ?? '',
    displayGetter: (row, _i, showModal) => renderCellValue('oldValue', row, showModal),
  },
  {
    header: 'Updated Value',
    label: 'newValue',
    valueGetter: (row: Partial<KYC_REPORT_TYPE>): string =>
      uboTypesMdms ? getUboDeclarationValues(uboTypesMdms, row).newValue : row.newValue ?? '',
    displayGetter: (row, _i, showModal) => renderCellValue('newValue', row, showModal),
  },
];

const headersForCvlKraFetch = [
  {
    header: 'S.No.',
    label: 'sno',
    valueGetter: (_: CVL_KRA_REPORT_TYPE, index: number): number => Number(index) + 1,
  },
  { header: 'Application Number', label: 'applicationNumber' },
  { header: 'Investor Name', label: 'applicantName' },
  { header: 'Initialized By', label: 'initializedBy' },
  { header: 'Scheme Name', label: 'scheme' },
  {
    header: 'Remarks',
    label: 'remarks',
  },
  { header: 'Differences', label: 'differences' },
];

export const onboardingDetailsDownload = [
  'Export',
  'Download Current Fields',
  'Download All Fields',
];

export const reportData: {
  value: string;
  key: string;
  tableHeaders: (fatca?: nonIndividualMdmsQuestionsFatca, uboTypesMdms?: uboTypes) => ColumnType[];
}[] = [
  {
    value: 'onboardingSummary',
    key: 'Onboarding Summary',
    tableHeaders: () => commonHeadersForSummaryReports,
  },
  {
    value: 'onboardingDetails',
    key: 'Onboarding Details',
    tableHeaders: () => [
      {
        header: 'First Holder Name',
        label: 'firstHolderName',
        valueGetter: (row: OnBoardingReportDetailsType) => row.firstHolderName || 'NA',
      },
      {
        header: 'Application ID',
        label: 'applicationNumber',
        valueGetter: (row: OnBoardingReportDetailsType) => row.applicationNumber || 'NA',
      },
      {
        header: 'Mode of Holding',
        valueGetter: (row: OnBoardingReportDetailsType) => row.modeOfHolding || 'NA',
      },
      {
        header: 'Scheme',
        valueGetter: (row: OnBoardingReportDetailsType) => row.scheme || 'NA',
      },
      {
        header: 'Date of onboarding',
        label: 'createdAt',
        valueGetter: (row: OnBoardingReportDetailsType) => formatShortDate(row.createdAt) || 'NA',
      },
      {
        header: 'Committed Value',
        valueGetter: (row: OnBoardingReportDetailsType) => row.commitmentAmount || 'NA',
      },
      {
        header: 'First Holder PAN',
        valueGetter: (row: OnBoardingReportDetailsType) => row.firstHolderPAN || 'NA',
      },
      {
        header: 'Distributor Name',
        valueGetter: (row: OnBoardingReportDetailsType) => row.distributor || 'NA',
      },
      {
        header: 'Leegality Document Id',
        valueGetter: (row: OnBoardingReportDetailsType) => row.leegalityDocumentId || 'NA',
      },
      {
        header: 'Status',
        valueGetter: ({ status }: OnBoardingReportDetailsType) =>
          APPLICATION_STATUS[status] || POA_APPLICATION_STATUS[status] || 'NA',
      },
    ],
  },
  {
    value: 'distributorOnBoardingSummary',
    key: 'Distributor wise onBoarding summary',
    tableHeaders: () => commonHeadersForSummaryReports,
  },
  // {
  //   value: 'distributorOnBoardingDetail',
  //   key: 'Distributor wise onBoarding details',
  //   tableHeaders: [],
  // },
  {
    value: 'distributorCommitmentAmount',
    key: 'Distributor wise commitment amount report',
    tableHeaders: () => [
      {
        header: 'Investor Type',
        label: 'investorType',
      },
      {
        header: 'Completed',
        label: 'completed',
      },
      {
        header: 'Commitment Amount',
        label: 'commitmentAmount',
      },
      {
        header: 'Contribution Amount',
        label: 'contributionAmount',
      },
    ],
  },
  {
    value: 'onboardingCompletedDetails',
    key: 'Onboarding Completed Details',
    tableHeaders: () => [
      {
        header: 'Application ID',
        label: 'applicationNumber',
        valueGetter: (row: OnBoardingCompletedReportDetailsType) => row.applicationNumber || 'NA',
      },
      {
        header: 'First Holder Name',
        label: 'firstHolderName',
        valueGetter: (row: OnBoardingCompletedReportDetailsType) => row.firstHolderName || 'NA',
      },
      {
        header: 'Folio Number',
        valueGetter: (row: OnBoardingCompletedReportDetailsType) => row.folio_no || 'NA',
      },
      {
        header: 'Distributor Name',
        valueGetter: (row: OnBoardingCompletedReportDetailsType) => row.distributor || 'NA',
      },
      {
        header: 'First Holder PAN',
        valueGetter: (row: OnBoardingCompletedReportDetailsType) => row.firstHolderPAN || 'NA',
      },
      {
        header: 'Date of completion',
        label: 'updatedAt',
        valueGetter: (row: OnBoardingCompletedReportDetailsType) =>
          formatShortDate(row.updatedAt) || 'NA',
      },
    ],
  },
  {
    value: 'amendmentReports',
    key: 'Amendment reports',
    tableHeaders: () => headersForAmendmentReports,
  },
  {
    value: 'changeRequests',
    key: 'Change Requests',
    tableHeaders: (fatca?: nonIndividualMdmsQuestionsFatca, uboTypesMdms?: uboTypes) =>
      headersForOnboardingCompletedKyc(
        fatca as nonIndividualMdmsQuestionsFatca,
        uboTypesMdms as uboTypes
      ),
  },
  {
    value: 'cvlKraFetch',
    key: 'CVL KRA fetch',
    tableHeaders: () => headersForCvlKraFetch,
  },
];

export const getFromDate = (): string => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString();
};
export const reportDownload = (res: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(res as unknown as Blob);
  FileSaver.saveAs(url, fileName);
};

export const filterApplicationStatusTypes = [
  { key: 'draft', value: 'Draft' },
  { key: 'sent_to_applicant1', value: 'Sent to 1st applicant' },
  { key: 'approved_by_applicant1', value: 'Approved by 1st Applicant' },
  { key: 'approved_by_applicant2', value: 'Approved by 2nd Applicant' },
  { key: 'approved_by_applicant3', value: 'Approved by 3rd Applicant' },
  { key: 'approved_by_fundmanager', value: 'Approved by investment manager' },
  { key: 'invitationexpired', value: 'Invitation Expired' },
  { key: 'completed,signed', value: 'Completed' },
  { key: 'rejected', value: 'Rejected' },
  {
    key: 'sent_to_poa_approver',
    value: 'Sent to POA approver',
  },
  {
    key: 'sent_to_reviewer',
    value: 'Sent to Reviewer',
  },
  {
    key: 'sent_to_amc_approver',
    value: 'Sent to Approver 1',
  },
  {
    key: 'sent_to_amc_approver2',
    value: 'Sent to Approver 2',
  },
  { key: 'sent_to_authorised_signatories', value: 'Sent to authorised signatories' },
  { key: 'approved_by_authorised_signatories', value: 'Approved by authorised signatories' },
];

export const filterNctApplicationStatusTypes = (role: string): { key: string; value: string }[] => [
  { key: 'draft', value: 'Draft' },
  { key: 'completed', value: 'Completed' },
  { key: 'rejected', value: 'Rejected' },
  {
    key: 'sent_to_amc_approver',
    value: role === 'amcapprover' ? 'Pending' : 'Sent to AIF approver',
  },
];
